import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { convertMediaUrl } from '../../config/environment';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Box,
  Tooltip,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import {
  MoreVert,
  PlayArrow,
  Download,
  Edit,
  Delete,
  Visibility,
  Schedule,
  VideoFile,
  Image,
  AudioFile,
  InsertDriveFile,
  Transform,
} from '@mui/icons-material';

import { MediaType, MediaStatus } from "../../types";
import type { Media } from "../../types";

interface MediaCardProps {
  media: Media;
  onView?: (media: Media) => void;
  onEdit?: (media: Media) => void;
  onDelete?: (media: Media) => void;
  onDownload?: (media: Media) => void;
  onPreview?: (media: Media) => void;
  onConvert?: (media: Media) => void;
}

const MediaCard: React.FC<MediaCardProps> = ({
  media,
  onView,
  onEdit,
  onDelete,
  onDownload,
  onPreview,
  onConvert,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [imageError, setImageError] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const getFileTypeIcon = (type: MediaType) => {
    switch (type) {
      case MediaType.VIDEO:
        return VideoFile;
      case MediaType.IMAGE:
        return Image;
      case MediaType.AUDIO:
        return AudioFile;
      default:
        return InsertDriveFile;
    }
  };

  const getStatusColor = (status: MediaStatus) => {
    switch (status) {
      case MediaStatus.READY:
        return 'success';
      case MediaStatus.PROCESSING:
        return 'warning';
      case MediaStatus.ERROR:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: MediaStatus) => {
    return t(`media.statuses.${status.toLowerCase()}`);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getThumbnailUrl = (): string => {
    // Используем thumbnail_url если доступен, конвертируя URL для правильного адреса
    if (media.thumbnail_url) {
      return convertMediaUrl(media.thumbnail_url);
    }
    
    // Для изображений используем основной файл, также конвертируя URL
    if (media.type === MediaType.IMAGE && media.file_url) {
      return convertMediaUrl(media.file_url);
    }
    
    return ''; // Для других типов файлов будем показывать иконку
  };

  const shouldShowThumbnail = (): boolean => {
    const thumbnailUrl = getThumbnailUrl();
    return !imageError && thumbnailUrl !== '';
  };

  // Обработчик ошибки загрузки изображения
  const handleImageError = () => {
    console.log(`Ошибка загрузки thumbnail для медиа ${media.id}: ${getThumbnailUrl()}`);
    setImageError(true);
  };

  const FileTypeIcon = getFileTypeIcon(media.type);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      {/* Превью медиафайла */}
      <Box
        sx={{
          position: 'relative',
          paddingTop: '56.25%', // 16:9 Aspect Ratio
          backgroundColor: 'grey.100',
          overflow: 'hidden',
        }}
      >
        {shouldShowThumbnail() ? (
          <CardMedia
            component="img"
            image={getThumbnailUrl()}
            alt={media.title}
            onError={handleImageError}
            onLoad={() => setImageError(false)} // Сбрасываем ошибку при успешной загрузке
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha('#1976d2', 0.1),
            }}
          >
            <FileTypeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          </Box>
        )}

        {/* Статус медиафайла */}
        <Chip
          label={getStatusLabel(media.status)}
          color={getStatusColor(media.status)}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(4px)',
          }}
        />

        {/* Кнопка воспроизведения для видео */}
        {media.type === MediaType.VIDEO && (
          <IconButton
            onClick={() => onPreview?.(media)}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <PlayArrow sx={{ fontSize: 32 }} />
          </IconButton>
        )}

        {/* Длительность для видео/аудио */}
        {(media.type === MediaType.VIDEO || media.type === MediaType.AUDIO) && media.duration && (
          <Chip
            label={formatDuration(media.duration)}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontSize: '0.75rem',
            }}
          />
        )}
      </Box>

      {/* Содержимое карточки */}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Typography
          variant="subtitle1"
          component="h3"
          fontWeight="medium"
          sx={{
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {media.title}
        </Typography>

        {media.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {media.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(media.file_size)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {media.type?.toUpperCase() || 'UNKNOWN'}
          </Typography>
        </Box>

        {media.tags && media.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {media.tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            ))}
            {media.tags.length > 3 && (
              <Chip
                label={`+${media.tags.length - 3}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
        )}
      </CardContent>

      {/* Действия с медиафайлом */}
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onView && (
            <Tooltip title={t('common.view')}>
              <IconButton size="small" onClick={() => onView(media)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {media.type === MediaType.VIDEO && onPreview && (
            <Tooltip title={t('media.preview')}>
              <IconButton size="small" onClick={() => onPreview(media)}>
                <PlayArrow fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <IconButton size="small" onClick={handleMenuOpen}>
          <MoreVert fontSize="small" />
        </IconButton>
      </CardActions>

      {/* Контекстное меню */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {onEdit && (
          <MenuItem onClick={() => handleAction(() => onEdit(media))}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit')}</ListItemText>
          </MenuItem>
        )}

        {onDownload && (
          <MenuItem onClick={() => handleAction(() => onDownload(media))}>
            <ListItemIcon>
              <Download fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.download')}</ListItemText>
          </MenuItem>
        )}

        {onConvert && (media.media_type === 'video' || media.media_type === 'audio' || media.type === 'video' || media.type === 'audio') && (
          <MenuItem onClick={() => handleAction(() => onConvert(media))}>
            <ListItemIcon>
              <Transform fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('media.conversion.title')}</ListItemText>
          </MenuItem>
        )}

        {onDelete && (
          <MenuItem onClick={() => handleAction(() => onDelete(media))}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default MediaCard; 
 
 
 